/**
 * Category Image Mappings
 *
 * Placeholder images from Unsplash for categories
 * These are used when category.icon is null or not a valid URL
 */

/**
 * Category slug to image URL mapping
 * Using high-quality images from Unsplash
 */
export const CATEGORY_IMAGE_MAP: Record<string, string> = {
  // Handyman & Home Services
  'handyman-home-services': '/images/categories/handyman-home-services.png',
  'handyman': '/images/categories/handyman-home-services.png',

  // Home Cleaning
  'home-cleaning': '/images/categories/home-cleaning.png',
  'cleaning': '/images/categories/home-cleaning.png',

  // Moving & Delivery
  'moving-delivery': '/images/categories/moving-delivery.png',
  'moving': '/images/categories/moving-delivery.png',
  'delivery': '/images/categories/moving-delivery.png',

  // Renovation & Trades
  'renovation-trades': '/images/categories/renovation-trades.png',
  'renovation': '/images/categories/renovation-trades.png',

  // Yard & Seasonal Services
  'yard-seasonal-services': '/images/categories/yard-seasonal-services.png',
  'yard': '/images/categories/yard-seasonal-services.png',
  'seasonal': '/images/categories/yard-seasonal-services.png',
};

/**
 * Default fallback image for categories without specific mapping
 */
const DEFAULT_CATEGORY_IMAGE = '/images/categories/handyman-home-services.png';

/**
 * Get category image URL
 *
 * Priority:
 * 1. If imageUrl is provided from server, use it (highest priority)
 * 2. If icon is a valid URL, use it
 * 3. If icon matches a slug in the map, use mapped image
 * 4. If slug matches a key in the map, use mapped image
 * 5. Use default fallback image
 *
 * @param icon - Category icon field (may be null, icon name, or URL)
 * @param slug - Category slug
 * @param imageUrl - Direct image URL from server (optional, highest priority)
 * @returns Image URL to use for the category
 */
export function getCategoryImageUrl(
  icon: string | null | undefined,
  slug: string,
  imageUrl?: string | null
): string {
  // Priority 1: Use imageUrl from server if provided
  if (imageUrl && (imageUrl.startsWith('http://') || imageUrl.startsWith('https://') || imageUrl.startsWith('/'))) {
    return imageUrl;
  }

  // Priority 2: If icon is a valid URL, use it
  if (icon && (icon.startsWith('http://') || icon.startsWith('https://') || icon.startsWith('/'))) {
    return icon;
  }

  // Priority 3: Try to find by icon value (if it's a slug-like string)
  if (icon && CATEGORY_IMAGE_MAP[icon.toLowerCase()]) {
    return CATEGORY_IMAGE_MAP[icon.toLowerCase()];
  }

  // Priority 4: Try to find by slug
  const slugKey = slug.toLowerCase();
  if (CATEGORY_IMAGE_MAP[slugKey]) {
    return CATEGORY_IMAGE_MAP[slugKey];
  }

  // Try partial matching (e.g., "yard-seasonal-services-x" matches "yard-seasonal-services")
  const partialMatch = Object.keys(CATEGORY_IMAGE_MAP).find(
    (key) => slugKey.includes(key) || key.includes(slugKey)
  );

  if (partialMatch) {
    return CATEGORY_IMAGE_MAP[partialMatch];
  }

  // Priority 5: Return default fallback
  return DEFAULT_CATEGORY_IMAGE;
}

/**
 * Check if a URL is a valid image URL
 */
export function isValidImageUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  return url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/');
}
